import React, { useState, useRef } from 'react';
import { ChevronLeft, Sparkles, Wand2, Loader2, FileText, Layers, ImageIcon, Link2 } from 'lucide-react';
import { DEFAULT_LLM_MODEL } from '@/constants/models';
import { DEFAULT_IMAGE_MODEL } from '@/constants/imageModels';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface AutoTemplateFormData {
  name: string;
  description: string;
  targetWordCount: number;
  tone: 'professional' | 'casual' | 'authoritative' | 'conversational';
  includeFAQ: boolean;
  includeImages: boolean;
  includeList: boolean;
  listType: 'auto' | 'bullet' | 'numbered' | 'lettered';
  imageStyle: 'photorealistic' | 'illustration' | '3d' | 'minimalist';
  minSections?: number;
  maxSections?: number;
  // Image configuration
  minImages?: number;
  maxImages?: number;
  imagePosition: 'auto' | 'after-intro' | 'per-section' | 'end-only';
  // Link configuration
  linkStrategy: 'word-density' | 'auto' | 'fixed' | 'density-based';
  internalLinkCount?: number;
  externalLinkCount?: number;
  // Content limits
  maxFAQItems?: number;
  maxListItems?: number;
  maxCalloutsPerArticle?: number;
  // Model configuration
  languageModel?: string;
  imageModel?: string;
}

interface AutoTemplateConfigFormProps {
  onSave: (config: AutoTemplateFormData) => void;
  onCancel: () => void;
  initialData?: Partial<AutoTemplateFormData>;
  isLoading?: boolean;
}

const DEFAULT_CONFIG: AutoTemplateFormData = {
  name: '',
  description: '',
  targetWordCount: 1500,
  tone: 'professional',
  includeFAQ: true,
  includeImages: true,
  includeList: true,
  listType: 'auto',
  imageStyle: 'photorealistic',
  minSections: undefined,
  maxSections: undefined,
  // Image configuration
  minImages: 1,
  maxImages: 3,
  imagePosition: 'auto',
  // Link configuration
  linkStrategy: 'word-density',
  internalLinkCount: undefined,
  externalLinkCount: undefined,
  // Content limits
  maxFAQItems: 5,
  maxListItems: 8,
  // Model configuration
  languageModel: DEFAULT_LLM_MODEL,
  imageModel: DEFAULT_IMAGE_MODEL,
};

const AI_PRESET: Partial<AutoTemplateFormData> = {
  targetWordCount: 1500,
  tone: 'professional',
  includeFAQ: true,
  includeImages: true,
  includeList: true,
  minSections: 4,
  maxSections: 7,
  minImages: 1,
  maxImages: 3,
  imagePosition: 'auto',
  linkStrategy: 'auto',
  internalLinkCount: 3,
  externalLinkCount: 2,
  maxFAQItems: 5,
};

export function AutoTemplateConfigForm({
  onSave,
  onCancel,
  initialData,
  isLoading = false,
}: AutoTemplateConfigFormProps) {
  const [config, setConfig] = useState<AutoTemplateFormData>({
    ...DEFAULT_CONFIG,
    ...initialData,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof AutoTemplateFormData, string>>>({});
  const [isSuggesting, setIsSuggesting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const handleAISuggest = () => {
    setIsSuggesting(true);
    // Simulate a brief delay for UX feedback
    setTimeout(() => {
      setConfig((prev) => ({
        ...prev,
        ...AI_PRESET,
        // Preserve user-entered name and description
        name: prev.name,
        description: prev.description,
      }));
      setIsSuggesting(false);
    }, 300);
  };

  const updateConfig = <K extends keyof AutoTemplateFormData>(
    key: K,
    value: AutoTemplateFormData[K]
  ) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    // Clear error when field is updated
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof AutoTemplateFormData, string>> = {};

    if (!config.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (config.targetWordCount < 300) {
      newErrors.targetWordCount = 'Minimum 300 words required';
    } else if (config.targetWordCount > 10000) {
      newErrors.targetWordCount = 'Maximum 10,000 words allowed';
    }

    if (config.minSections !== undefined && config.maxSections !== undefined) {
      if (config.minSections > config.maxSections) {
        newErrors.minSections = 'Min sections cannot be greater than max';
      }
    }

    if (config.minImages !== undefined && config.maxImages !== undefined) {
      if (config.minImages > config.maxImages) {
        newErrors.minImages = 'Min images cannot be greater than max';
      }
    }

    setErrors(newErrors);

    // Scroll to first error
    const hasErrors = Object.keys(newErrors).length > 0;
    if (hasErrors && formRef.current) {
      const firstErrorField = formRef.current.querySelector('.border-destructive');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    return !hasErrors;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(config);
    }
  };

  return (
    <TooltipProvider>
      <div ref={formRef} className="py-2 space-y-3 max-w-xl">
        {/* AI Suggest Button - Top Right */}
        <div className="flex justify-end">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAISuggest}
                disabled={isSuggesting}
                className={`${isSuggesting ? "" : "animate-ai-suggest"} gap-2 h-7`}
              >
                {isSuggesting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Wand2 className="h-3.5 w-3.5" />
                )}
                AI Suggest
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Auto-fill recommended template settings</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Template Info */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="name" className="text-xs">
              Template Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              placeholder="My Auto Template"
              value={config.name}
              onChange={(e) => updateConfig('name', e.target.value)}
              className={`h-8 ${errors.name ? 'border-destructive' : ''}`}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="description" className="text-xs">Description</Label>
            <Input
              id="description"
              placeholder="Brief description..."
              value={config.description}
              onChange={(e) => updateConfig('description', e.target.value)}
              className="h-8"
            />
          </div>
        </div>

        {/* Content Settings Card */}
        <div className="bg-muted/30 rounded-lg p-3 space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            Content Settings
          </h4>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="targetWordCount" className="text-xs">Word Count</Label>
              <Input
                id="targetWordCount"
                type="number"
                min={300}
                max={10000}
                step={100}
                value={config.targetWordCount}
                onChange={(e) =>
                  updateConfig('targetWordCount', parseInt(e.target.value) || 1500)
                }
                className={`h-8 ${errors.targetWordCount ? 'border-destructive' : ''}`}
              />
              {errors.targetWordCount && (
                <p className="text-xs text-destructive">{errors.targetWordCount}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="tone" className="text-xs">Tone</Label>
              <Select
                value={config.tone}
                onValueChange={(value) => updateConfig('tone', value as typeof config.tone)}
              >
                <SelectTrigger id="tone" className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="casual">Casual</SelectItem>
                  <SelectItem value="authoritative">Authoritative</SelectItem>
                  <SelectItem value="conversational">Conversational</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Structure Card */}
        <div className="bg-muted/30 rounded-lg p-3 space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <Layers className="h-3.5 w-3.5" />
            Structure
          </h4>

          <div className="grid grid-cols-4 gap-2">
            <div className="space-y-1">
              <Label htmlFor="minSections" className="text-xs">Min Sections</Label>
              <Input
                id="minSections"
                type="number"
                min={1}
                max={20}
                placeholder="Auto"
                value={config.minSections ?? ''}
                onChange={(e) =>
                  updateConfig(
                    'minSections',
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                className={`h-8 ${errors.minSections ? 'border-destructive' : ''}`}
              />
              {errors.minSections && (
                <p className="text-xs text-destructive">{errors.minSections}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="maxSections" className="text-xs">Max Sections</Label>
              <Input
                id="maxSections"
                type="number"
                min={1}
                max={20}
                placeholder="Auto"
                value={config.maxSections ?? ''}
                onChange={(e) =>
                  updateConfig(
                    'maxSections',
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                className="h-8"
              />
            </div>

            <div className="flex items-end pb-1">
              <div className="flex items-center gap-1.5">
                <Checkbox
                  id="includeFAQ"
                  checked={config.includeFAQ}
                  onCheckedChange={(checked) => updateConfig('includeFAQ', !!checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="includeFAQ" className="text-xs font-normal cursor-pointer">
                  FAQ Section
                </Label>
              </div>
            </div>

            <div className="flex items-end pb-1">
              <div className="flex items-center gap-1.5">
                <Checkbox
                  id="includeList"
                  checked={config.includeList}
                  onCheckedChange={(checked) => updateConfig('includeList', !!checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="includeList" className="text-xs font-normal cursor-pointer">
                  Include List
                </Label>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="listType" className="text-xs">List Type</Label>
              <Select
                value={config.listType}
                onValueChange={(value) =>
                  updateConfig('listType', value as typeof config.listType)
                }
                disabled={!config.includeList}
              >
                <SelectTrigger id="listType" className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto</SelectItem>
                  <SelectItem value="bullet">Bullet</SelectItem>
                  <SelectItem value="numbered">Numbered</SelectItem>
                  <SelectItem value="lettered">Lettered</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="maxListItems" className="text-xs">Max List Items</Label>
              <Input
                id="maxListItems"
                type="number"
                min={3}
                max={20}
                placeholder="8"
                value={config.maxListItems ?? ''}
                onChange={(e) =>
                  updateConfig(
                    'maxListItems',
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                className="h-8"
              />
            </div>
          </div>
        </div>

        {/* Images Card */}
        <div className="bg-muted/30 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <ImageIcon className="h-3.5 w-3.5" />
              Images
            </h4>
            <div className="flex items-center gap-1.5">
              <Checkbox
                id="includeImages"
                checked={config.includeImages}
                onCheckedChange={(checked) => updateConfig('includeImages', !!checked)}
                className="h-4 w-4"
              />
              <Label htmlFor="includeImages" className="text-xs font-normal cursor-pointer">
                Include AI-Generated Images
              </Label>
            </div>
          </div>

          {config.includeImages && (
            <div className="grid grid-cols-4 gap-2">
              <div className="space-y-1">
                <Label htmlFor="imageStyle" className="text-xs">Image Style</Label>
                <Select
                  value={config.imageStyle}
                  onValueChange={(value) =>
                    updateConfig('imageStyle', value as typeof config.imageStyle)
                  }
                >
                  <SelectTrigger id="imageStyle" className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="photorealistic">Photorealistic</SelectItem>
                    <SelectItem value="illustration">Illustration</SelectItem>
                    <SelectItem value="3d">3D Render</SelectItem>
                    <SelectItem value="minimalist">Minimalist</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="imagePosition" className="text-xs">Image Position</Label>
                <Select
                  value={config.imagePosition}
                  onValueChange={(value) =>
                    updateConfig('imagePosition', value as typeof config.imagePosition)
                  }
                >
                  <SelectTrigger id="imagePosition" className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (AI decides)</SelectItem>
                    <SelectItem value="after-intro">After Introduction</SelectItem>
                    <SelectItem value="per-section">One Per Section</SelectItem>
                    <SelectItem value="end-only">End Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="minImages" className="text-xs">Min Images</Label>
                <Input
                  id="minImages"
                  type="number"
                  min={0}
                  max={10}
                  value={config.minImages ?? ''}
                  onChange={(e) =>
                    updateConfig(
                      'minImages',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className={`h-8 ${errors.minImages ? 'border-destructive' : ''}`}
                />
                {errors.minImages && (
                  <p className="text-xs text-destructive">{errors.minImages}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="maxImages" className="text-xs">Max Images</Label>
                <Input
                  id="maxImages"
                  type="number"
                  min={0}
                  max={10}
                  value={config.maxImages ?? ''}
                  onChange={(e) =>
                    updateConfig(
                      'maxImages',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="h-8"
                />
              </div>
            </div>
          )}
        </div>

        {/* Links Card */}
        <div className="bg-muted/30 rounded-lg p-3 space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <Link2 className="h-3.5 w-3.5" />
            Link Configuration
          </h4>

          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label htmlFor="linkStrategy" className="text-xs">Link Strategy</Label>
              <Select
                value={config.linkStrategy}
                onValueChange={(value) =>
                  updateConfig('linkStrategy', value as typeof config.linkStrategy)
                }
              >
                <SelectTrigger id="linkStrategy" className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="word-density">Word Density (Recommended)</SelectItem>
                  <SelectItem value="auto">Auto (AI decides)</SelectItem>
                  <SelectItem value="fixed">Fixed Count</SelectItem>
                  <SelectItem value="density-based">Density Based</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="internalLinkCount" className="text-xs">Internal Links</Label>
              <Input
                id="internalLinkCount"
                type="number"
                min={0}
                max={20}
                placeholder={config.linkStrategy === 'auto' || config.linkStrategy === 'word-density' ? 'Auto' : '0'}
                value={config.internalLinkCount ?? ''}
                onChange={(e) =>
                  updateConfig(
                    'internalLinkCount',
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                disabled={config.linkStrategy === 'auto' || config.linkStrategy === 'word-density'}
                className="h-8"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="externalLinkCount" className="text-xs">External Links</Label>
              <Input
                id="externalLinkCount"
                type="number"
                min={0}
                max={10}
                placeholder={config.linkStrategy === 'auto' || config.linkStrategy === 'word-density' ? 'Auto' : '0'}
                value={config.externalLinkCount ?? ''}
                onChange={(e) =>
                  updateConfig(
                    'externalLinkCount',
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                disabled={config.linkStrategy === 'auto' || config.linkStrategy === 'word-density'}
                className="h-8"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-1">
          <Button variant="ghost" onClick={onCancel} className="gap-1 h-8">
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>

          <Button onClick={handleSave} className="gap-2 h-8" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Create Auto Template
              </>
            )}
          </Button>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default AutoTemplateConfigForm;
